import { useCallback, useEffect, useRef, useState } from 'react';
import { GameOver, Hud, Lobby, PauseOverlay } from '@/components/ui';
import { ChompTideScene, type SceneInput, type SceneStats } from '@/game/scene';
import {
  completePlaySession,
  emitGameEvent,
  startLooplayAttempt,
  trackLooplayView,
  trackMatch,
} from '@/looplay';
export type GamePhase = 'lobby' | 'playing' | 'paused' | 'gameOver';
interface RunResult {
  score: number;
  level: number;
  durationSeconds: number;
  isNewBest: boolean;
}
interface PointerStart {
  x: number;
  y: number;
  time: number;
  id: number;
}
const BEST_SCORE_KEY = 'chomp-tide-best';
const INITIAL_STATS: SceneStats = { level: 1, growth: 0, score: 0, dashCooldown: 0 };
const readBestScore = (): number => {
  try {
    const value = Number.parseInt(window.localStorage.getItem(BEST_SCORE_KEY) ?? '0', 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
};
export function HomePage() {
  const directPlay = window.location.hash === '#playing' || window.location.search.includes('screen=playing');
  const [phase, setPhase] = useState<GamePhase>(directPlay ? 'playing' : 'lobby');
  const sceneActive = phase === 'playing' || phase === 'paused';
  const [stats, setStats] = useState<SceneStats>(INITIAL_STATS);
  const [runResult, setRunResult] = useState<RunResult | null>(null);
  const [bestScore, setBestScore] = useState(readBestScore);
  const [sceneError, setSceneError] = useState<string | null>(null);
  const [sceneReady, setSceneReady] = useState(false);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<ChompTideScene | null>(null);
  const sessionStartedAtRef = useRef(0);
  const pointerStartRef = useRef<PointerStart | null>(null);
  const inputRef = useRef<SceneInput>({
    pointerActive: false,
    targetX: 0,
    targetY: 0,
    up: false,
    down: false,
    left: false,
    right: false,
    dash: false,
  });
  useEffect(() => {
    trackLooplayView();
  }, []);
  const beginRun = useCallback(() => {
    setRunResult(null);
    setStats(INITIAL_STATS);
    setSceneError(null);
    setSceneReady(false);
    const input = inputRef.current;
    input.pointerActive = false;
    input.up = false;
    input.down = false;
    input.left = false;
    input.right = false;
    input.dash = false;
    sessionStartedAtRef.current = performance.now();
    setPhase('playing');
    startLooplayAttempt();
  }, []);
  const handleGameOver = useCallback((finalStats: SceneStats) => {
    setPhase((current) => {
      if (current === 'gameOver') return current;
      const durationSeconds = Math.max(0, Math.round((performance.now() - sessionStartedAtRef.current) / 1000));
      const previousBest = readBestScore();
      const isNewBest = finalStats.score > previousBest;
      const nextBest = Math.max(previousBest, finalStats.score);
      try {
        window.localStorage.setItem(BEST_SCORE_KEY, String(nextBest));
      } catch (error) {
        console.warn('[Chomp Tide] Best score could not be persisted', error);
      }
      setBestScore(nextBest);
      setRunResult({ score: finalStats.score, level: finalStats.level, durationSeconds, isNewBest });
      const matchId = `chomp-tide-${sessionStartedAtRef.current}`;
      void completePlaySession();
      void trackMatch(matchId, { durationSeconds, isCompleted: true, isWin: false });
      void emitGameEvent('SCORE_SUBMITTED', {
        value: finalStats.score,
        refId: matchId,
        payload: { size: finalStats.level, durationSeconds, isNewBest },
      });
      return 'gameOver';
    });
  }, []);
  useEffect(() => {
    if (!sceneActive) return;
    const surface = surfaceRef.current;
    if (!surface) return;
    let cancelled = false;
    const scene = new ChompTideScene(
      surface,
      inputRef.current,
      (nextStats) => {
        if (!cancelled) {
          setStats(nextStats);
          setSceneReady(true);
        }
      },
      handleGameOver,
    );
    sceneRef.current = scene;
    void scene.init().catch((error: unknown) => {
      console.error('[Chomp Tide] Unable to initialize game scene', error);
      if (!cancelled) {
        setSceneReady(false);
        setSceneError('Artwork could not be loaded. Please restart the dive.');
      }
    });
    return () => {
      cancelled = true;
      scene.destroy();
      if (sceneRef.current === scene) sceneRef.current = null;
    };
  }, [handleGameOver, sceneActive]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (phase === 'gameOver' && key === 'r') {
        beginRun();
        return;
      }
      if (phase !== 'playing' && phase !== 'paused') return;
      if (['arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'w', 'a', 's', 'd'].includes(key)) {
        event.preventDefault();
        const input = inputRef.current;
        input.up ||= key === 'arrowup' || key === 'w';
        input.down ||= key === 'arrowdown' || key === 's';
        input.left ||= key === 'arrowleft' || key === 'a';
        input.right ||= key === 'arrowright' || key === 'd';
      }
      if ((key === ' ' || key === 'enter') && !event.repeat && phase === 'playing') {
        event.preventDefault();
        inputRef.current.dash = true;
      }
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const input = inputRef.current;
      input.up = key !== 'arrowup' && key !== 'w' && input.up;
      input.down = key !== 'arrowdown' && key !== 's' && input.down;
      input.left = key !== 'arrowleft' && key !== 'a' && input.left;
      input.right = key !== 'arrowright' && key !== 'd' && input.right;
    };
    const releaseInput = () => {
      inputRef.current.pointerActive = false;
      inputRef.current.up = false;
      inputRef.current.down = false;
      inputRef.current.left = false;
      inputRef.current.right = false;
      inputRef.current.dash = false;
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', releaseInput);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', releaseInput);
    };
  }, [beginRun, phase]);
  const updatePointer = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const input = inputRef.current;
    input.targetX = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
    input.targetY = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
  }, []);
  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (phase !== 'playing' || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointerStartRef.current = { x: event.clientX, y: event.clientY, time: performance.now(), id: event.pointerId };
    inputRef.current.pointerActive = true;
    updatePointer(event);
  }, [phase, updatePointer]);
  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const pointer = pointerStartRef.current;
    if (phase !== 'playing' || !pointer || pointer.id !== event.pointerId) return;
    const distance = Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y);
    if (distance > 10) {
      inputRef.current.pointerActive = true;
      updatePointer(event);
    }
  }, [phase, updatePointer]);
  const handlePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const pointer = pointerStartRef.current;
    if (!pointer || pointer.id !== event.pointerId) return;
    const distance = Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y);
    const duration = performance.now() - pointer.time;
    if (phase === 'playing' && distance <= 10 && duration <= 280) inputRef.current.dash = true;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    pointerStartRef.current = null;
    inputRef.current.pointerActive = false;
  }, [phase]);
  const pauseGame = useCallback(() => {
    if (phase !== 'playing') return;
    sceneRef.current?.pause();
    inputRef.current.pointerActive = false;
    setPhase('paused');
  }, [phase]);
  const resumeGame = useCallback(() => {
    if (phase !== 'paused') return;
    sceneRef.current?.resume();
    setPhase('playing');
  }, [phase]);
  return (
    <main className="relative mx-auto flex h-[100dvh] w-full max-w-[430px] flex-col overflow-hidden bg-[#082f49] text-white">
      {sceneActive ? (
        <div className="relative h-full w-full flex-1">
          <div
            ref={surfaceRef}
            className="relative h-full w-full touch-none select-none overflow-hidden bg-[#082f49]"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            aria-label="Chomp Tide game surface"
          />
          {!sceneReady && !sceneError ? (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#082f49] px-6 text-center text-white">
              <div aria-live="polite">
                <p className="text-xl font-extrabold text-white">Entering the tide…</p>
                <p className="mt-2 text-sm font-bold text-cyan-100">Loading the reef and fish</p>
                <div className="mx-auto mt-5 h-2 w-36 overflow-hidden rounded-full bg-white/15">
                  <div className="h-full w-1/2 animate-pulse rounded-full bg-[#fbbf24]" />
                </div>
              </div>
            </div>
          ) : null}
          {phase === 'playing' ? (
            <Hud
              level={stats.level}
              score={stats.score}
              growth={stats.growth}
              dashCooldown={stats.dashCooldown}
              paused={false}
              onPause={pauseGame}
            />
          ) : null}
          {phase === 'paused' ? <PauseOverlay onResume={resumeGame} onRestart={beginRun} /> : null}
          {sceneError ? (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-[#082f49]/90 px-6 text-center text-white">
              <div>
                <p className="text-xl font-extrabold">Unable to enter the tide</p>
                <p className="mt-2 text-sm text-cyan-100">{sceneError}</p>
                <button type="button" onClick={beginRun} className="mt-5 min-h-14 cursor-pointer rounded-2xl bg-[#fb7185] px-6 font-extrabold text-[#082f49] active:scale-95">Retry</button>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="relative h-full w-full">
          <img src="/assets/background.jpg" alt="Sunlit underwater reef" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#082f49]/55" />
          {phase === 'lobby' ? (
            <Lobby bestScore={bestScore} onStart={beginRun} />
          ) : runResult ? (
            <GameOver result={runResult} bestScore={bestScore} onRestart={beginRun} />
          ) : null}
        </div>
      )}
    </main>
  );
}
